import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  ChevronDown,
  Image,
  Smartphone,
  Sparkles,
} from 'lucide-react';
import { DeviceFrame, HeroScene, ScenarioArt } from './home-art';
import {
  FAQS,
  FEATURES,
  FLOATERS,
  HERO_COPY,
  HERO_MEDIA,
  PLAY_STORE_URL,
  PRICING,
  SCENARIOS,
  SCREENSHOTS,
  STEPS,
} from './home-content';
import type { SectionStatus } from './use-mock-section';

const Skel: React.FC<{ className?: string }> = ({ className = '' }) => (
  <span className={`ml-skel${className ? ` ${className}` : ''}`} />
);

const PlayMark: React.FC = () => (
  <svg className="ml-play__mark" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M3.2 20.6V3.4L13.2 12 3.2 20.6z" />
    <path fill="#34A853" d="M16.4 14.6 13.2 12 3.2 20.6c.4.7 1.2.9 1.9.5l11.3-6.5z" />
    <path fill="#FBBC04" d="M16.4 9.4 5.1 2.9c-.7-.4-1.5-.2-1.9.5L13.2 12l3.2-2.6z" />
    <path fill="#EA4335" d="M20.6 10.6 16.4 8.2 13.2 12l3.2 2.6 4.2-2.4c.8-.4.8-1.6 0-2z" />
  </svg>
);

const PlayStoreButton: React.FC<{ id?: string }> = ({ id }) => (
  <a id={id} className="ml-play" href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
    <PlayMark />
    <span>
      <small>GET IT ON</small>
      <strong>Google Play</strong>
    </span>
  </a>
);

const LineLink: React.FC<{ href: string; ghost?: boolean }> = ({ href, ghost = false }) => (
  <a className={`ml-line${ghost ? ' ml-line--ghost' : ''}`} href={href}>
    <Image size={16} aria-hidden="true" />
    View Screenshots
  </a>
);

const HeadSkel: React.FC<{ center?: boolean }> = ({ center = false }) => (
  <div className={`ml-section__head${center ? ' ml-section__head--center' : ''}`}>
    <div>
      <Skel className="ml-skel--h" />
      <Skel className="ml-skel--sub" />
    </div>
  </div>
);

const ShotSet: React.FC<{ copy: 'main' | 'loop' }> = ({ copy }) => (
  <div className="ml-marquee__set" aria-hidden={copy === 'loop' ? true : undefined}>
    {SCREENSHOTS.map((shot) => (
      <img
        key={`${copy}-${shot.id}`}
        className="ml-shot"
        src={shot.src}
        alt={copy === 'loop' ? '' : shot.alt}
        width={520}
        height={924}
        decoding="async"
      />
    ))}
  </div>
);

type HeroProps = { status: SectionStatus };

export const HeroSection: React.FC<HeroProps> = ({ status }) => {
  const label = status === 'loading' ? 'Loading introduction' : 'Introduction';
  return (
    <section className="ml-hero" id="top" aria-label={label} aria-busy={status === 'loading'}>
      {status === 'ready' && (
        <>
          <HeroScene image={HERO_MEDIA.scene} label="Illustrated city map with a route" />
          <div className="ml-hero__scrim" aria-hidden="true" />
        </>
      )}
      <div className="ml-wrap ml-hero__grid">
        {status === 'loading' ? (
          <div aria-hidden="true">
            <Skel className="ml-skel--badge" />
            <Skel className="ml-skel--title" />
            <Skel className="ml-skel--title ml-skel--short" />
            <Skel className="ml-skel--line" />
            <Skel className="ml-skel--line ml-skel--mid" />
            <div className="ml-hero__actions">
              <Skel className="ml-skel--btn" />
              <Skel className="ml-skel--btn" />
            </div>
          </div>
        ) : (
          <div className="ml-hero__copy">
            <p className="ml-badge">
              <span className="ml-badge__icon">
                <Sparkles size={14} aria-hidden="true" />
              </span>
              {HERO_COPY.badge}
            </p>
            <h1>
              <span className="ml-hero__lead">{HERO_COPY.titleLead}</span>
              <span className="ml-hero__accent">{HERO_COPY.titleAccent}</span>
            </h1>
            <p className="ml-hero__body">{HERO_COPY.body}</p>
            <div className="ml-hero__actions">
              <PlayStoreButton id="cta-play-store" />
              <LineLink href="#screenshots" />
            </div>
          </div>
        )}
        <div className="ml-hero__stage">
          {status === 'loading' ? (
            <>
              <Skel className="ml-skel--phone" />
              <div className="ml-floaters" aria-hidden="true">
                {FLOATERS.map((item) => (
                  <Skel key={item.id} className="ml-skel--chip" />
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="ml-hero__phone">
                {HERO_MEDIA.phone ? (
                  <img
                    className="ml-hero__device"
                    src={HERO_MEDIA.phone}
                    alt="MockLoc app on a phone"
                    width={458}
                    height={1024}
                  />
                ) : (
                  <DeviceFrame
                    slot="hero-phone"
                    label="MockLoc app on a phone"
                    variant="hero"
                  />
                )}
              </div>
              <div className="ml-floaters">
                {FLOATERS.map((item) => {
                  const Icon = item.Icon;
                  return (
                    <div key={item.id} className="ml-floater ml-tone" data-tone={item.tone}>
                      <span className="ml-floater__icon">
                        <Icon size={18} aria-hidden="true" />
                      </span>
                      <span>{item.label}</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

type ShotProps = { status: SectionStatus };

export const ScreenshotSection: React.FC<ShotProps> = ({ status }) => {
  const label = status === 'loading' ? 'Loading app screenshots' : 'App screenshots';

  return (
    <section className="ml-section" id="screenshots" aria-label={label} aria-busy={status === 'loading'}>
      <div className="ml-wrap">
        {status === 'loading' ? (
          <>
            <HeadSkel />
            <div className="ml-marquee ml-marquee--skel" aria-hidden="true">
              {Array.from({ length: 4 }, (_, index) => (
                <Skel key={index} className="ml-skel--shot" />
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="ml-section__head">
              <h2>App Screenshots</h2>
              <p className="ml-lead">See MockLoc in action. Explore real screens from the app.</p>
            </div>
            <div className="ml-marquee">
              <div className="ml-marquee__track">
                <ShotSet copy="main" />
                <ShotSet copy="loop" />
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export const FeatureSection: React.FC<{ status: SectionStatus }> = ({ status }) => {
  const label = status === 'loading' ? 'Loading features' : 'Features';
  return (
    <section className="ml-section" id="features" aria-label={label} aria-busy={status === 'loading'}>
      <div className="ml-wrap">
        {status === 'loading' ? (
          <>
            <HeadSkel center />
            <div className="ml-features" aria-hidden="true">
              {FEATURES.map((item) => (
                <Skel key={item.id} className="ml-skel--feature" />
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="ml-section__head ml-section__head--center">
              <h2>Powerful Features for Every Use Case</h2>
              <p className="ml-lead">Everything you need to mock, simulate and test GPS location with ease.</p>
            </div>
            <div className="ml-features">
              {FEATURES.map((feature) => {
                const Icon = feature.Icon;
                return (
                  <article key={feature.id} className="ml-feature ml-tone" data-tone={feature.tone}>
                    <span className="ml-feature__icon">
                      <Icon size={22} aria-hidden="true" />
                    </span>
                    <h3 className="ml-feature__title">{feature.title}</h3>
                    <p className="ml-feature__desc">{feature.desc}</p>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export const SetupSection: React.FC<{ status: SectionStatus }> = ({ status }) => {
  const label = status === 'loading' ? 'Loading setup steps' : 'How to set up MockLoc';
  return (
    <section className="ml-section" id="how-it-works" aria-label={label} aria-busy={status === 'loading'}>
      <div className="ml-wrap">
        {status === 'loading' ? (
          <>
            <HeadSkel center />
            <div className="ml-steps" aria-hidden="true">
              {STEPS.map((step) => (
                <Skel key={step.id} className="ml-skel--step" />
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="ml-section__head ml-section__head--center ml-setup__head">
              <p className="ml-setup__badge">
                <Smartphone size={14} aria-hidden="true" />
                Quick & easy setup
              </p>
              <h2>Get started on your Android phone</h2>
              <p className="ml-lead">Install MockLoc, allow mock location, then open the app you want to test.</p>
            </div>
            <ol className="ml-steps">
              {STEPS.map((step) => {
                const Icon = step.Icon;
                return (
                  <li key={step.id} className="ml-step ml-tone" data-tone={step.tone}>
                    <span className="ml-step__mark">
                      <span className="ml-step__num">{step.n}</span>
                      <span className="ml-step__icon" aria-hidden="true">
                        <Icon size={16} />
                      </span>
                    </span>
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </li>
                );
              })}
            </ol>
          </>
        )}
      </div>
    </section>
  );
};

export const ScenarioSection: React.FC<{ status: SectionStatus }> = ({ status }) => {
  const label = status === 'loading' ? 'Loading scenarios' : 'Scenarios';
  return (
    <section className="ml-section" id="scenarios" aria-label={label} aria-busy={status === 'loading'}>
      <div className="ml-wrap">
        {status === 'loading' ? (
          <>
            <HeadSkel center />
            <div className="ml-scenarios" aria-hidden="true">
              {SCENARIOS.map((item) => (
                <Skel key={item.id} className="ml-skel--scenario" />
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="ml-section__head ml-section__head--center">
              <h2>Useful for Many Scenarios</h2>
              <p className="ml-lead">MockLoc helps developers, gamers, testers and everyday users.</p>
            </div>
            <div className="ml-scenarios">
              {SCENARIOS.map((item) => {
                const Icon = item.Icon;
                return (
                  <article key={item.id} className="ml-scenario">
                    <div className="ml-scenario__media">
                      <ScenarioArt kind={item.kind} slot={item.slot} label={item.label} image={item.image} />
                      <span className="ml-scenario__badge ml-tone" data-tone={item.tone}>
                        <Icon size={18} aria-hidden="true" />
                      </span>
                    </div>
                    <div className="ml-scenario__body">
                      <h3>{item.title}</h3>
                      <p>{item.desc}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export const PossibilitiesSection: React.FC<{ status: SectionStatus }> = ({ status }) => {
  const label = status === 'loading' ? 'Loading possibilities' : 'Endless possibilities';
  return (
    <section className="ml-section" aria-label={label} aria-busy={status === 'loading'}>
      <div className="ml-wrap">
        {status === 'loading' ? (
          <Skel className="ml-skel--band ml-skel--possibilities" />
        ) : (
          <picture>
            <source media="(max-width: 740px)" srcSet="/endless-possibilities-mobile.jpg" />
            <img
              className="ml-band-art"
              src="/endless-possibilities.jpg"
              alt="Endless Possibilities. From testing to gaming to research — MockLoc opens up a world of possibilities."
              width={1024}
              height={341}
            />
          </picture>
        )}
      </div>
    </section>
  );
};

type PricingProps = {
  status: SectionStatus;
  actionTo: string;
  actionLabel: string;
};

export const PricingSection: React.FC<PricingProps> = ({ status, actionTo, actionLabel }) => {
  const label = status === 'loading' ? 'Loading pricing' : 'Pricing';
  return (
    <section className="ml-section" id="pricing" aria-label={label} aria-busy={status === 'loading'}>
      <div className="ml-wrap">
        {status === 'loading' ? (
          <>
            <HeadSkel center />
            <Skel className="ml-skel--plan" />
          </>
        ) : (
          <>
            <div className="ml-section__head ml-section__head--center">
              <h2>{PRICING.title}</h2>
              <p className="ml-lead">{PRICING.lead}</p>
            </div>
            <article className="ml-plan">
              <p className="ml-plan__price">{PRICING.price}</p>
              <p className="ml-plan__note">{PRICING.note}</p>
              <ul>
                {PRICING.items.map((item) => (
                  <li key={item}>
                    <Check size={16} aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link to={actionTo} className="ml-signin" id="pricing-cta">
                {actionLabel}
              </Link>
            </article>
          </>
        )}
      </div>
    </section>
  );
};

export const HelpSection: React.FC<{ status: SectionStatus }> = ({ status }) => {
  const [open, setOpen] = useState<number | null>(0);
  const label = status === 'loading' ? 'Loading help' : 'Help';

  return (
    <section className="ml-section" id="help" aria-label={label} aria-busy={status === 'loading'}>
      <div className="ml-wrap">
        {status === 'loading' ? (
          <>
            <HeadSkel center />
            <div className="ml-faq" aria-hidden="true">
              {FAQS.map((item) => (
                <Skel key={item.q} className="ml-skel--row" />
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="ml-section__head ml-section__head--center">
              <h2>Questions, answered</h2>
              <p className="ml-lead">Quick answers before you install MockLoc.</p>
            </div>
            <div className="ml-faq">
              {FAQS.map((item, index) => {
                const isOpen = open === index;
                const panelId = `faq-panel-${index}`;
                return (
                  <div key={item.q} className={`ml-faq__item${isOpen ? ' is-open' : ''}`}>
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => setOpen(isOpen ? null : index)}
                    >
                      {item.q}
                      <ChevronDown size={18} aria-hidden="true" />
                    </button>
                    {isOpen && (
                      <p id={panelId} className="ml-faq__answer">
                        {item.a}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="ml-help__more">
              <Link to="/articles">Read setup articles</Link>
            </p>
          </>
        )}
      </div>
    </section>
  );
};

export const ClosingSection: React.FC<{ status: SectionStatus }> = ({ status }) => {
  const label = status === 'loading' ? 'Loading download call to action' : 'MockLoc across every device';
  return (
    <section className="ml-section" aria-label={label} aria-busy={status === 'loading'}>
      <div className="ml-wrap">
        {status === 'loading' ? (
          <Skel className="ml-skel--band ml-skel--close" />
        ) : (
          <picture>
            <source media="(max-width: 740px)" srcSet="/ready-explore-mobile.jpg" />
            <img
              className="ml-band-art"
              src="/ready-explore.jpg"
              alt="MockLoc Across Every Device. Use MockLoc on your Android phone, tablet, or multiple devices. Sync your data and enjoy a seamless experience everywhere."
              width={1024}
              height={512}
            />
          </picture>
        )}
      </div>
    </section>
  );
};
